import React, { useState } from 'react';
import { useFormik, FormikProvider } from 'formik';
import { useDispatch } from 'react-redux';
import { addNotification } from '../../../redux/slices/NotificationSlice';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import DropdownField from '../../../shared/DropdownField';
import { InputTextarea } from 'primereact/inputtextarea';
import TextInput from '../../../shared/TextInput';
import CustomChip from '../../../shared/CustomChip';
import { Timeline } from 'primereact/timeline';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import Button from '../../../shared/CustomButton';
import { 
  FaSave, 
  FaUser, 
  FaClock, 
  FaComments, 
  FaEye,
  FaPaperclip,
  FaEdit,
  FaArrowUp,
  FaLink,
  FaTags,
  FaCalendar
} from 'react-icons/fa';
import { 
  ticketPriorities, 
  ticketStatuses
} from '../../../constants/QueueManagementConstants';

// Helper to update ticket in localStorage
function updateTicketInLocalStorage(updatedTicket) {
  const tickets = JSON.parse(localStorage.getItem('tickets') || '[]');
  const idx = tickets.findIndex(t => t.ticketId === updatedTicket.ticketId);
  if (idx !== -1) {
    tickets[idx] = updatedTicket;
    localStorage.setItem('tickets', JSON.stringify(tickets));
  }
}

export default function TicketDetail({ ticket, onClose, onUpdate }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [editMode, setEditMode] = useState(false);
  const [ticketData, setTicketData] = useState({ ...ticket });
  // Formik for staging key editable fields (status, priority, assignee)
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      status: ticket.status || '',
      priority: ticket.priority || '',
      assignee: ticket.assignee || ''
    },
    onSubmit: () => {
      handleSave();
    }
  });
  const [stagedComments, setStagedComments] = useState([]);
  const [stagedInternalNotes, setStagedInternalNotes] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [newInternalNote, setNewInternalNote] = useState('');

  const handleSave = () => {
    const { status, priority, assignee } = formik.values;
    let updatedTicket = { 
      ...ticketData, 
      status, 
      priority, 
      assignee, 
      lastUpdated: new Date().toISOString() 
    };
    let statusHistory = [...(ticketData.statusHistory || [])];
    let assignmentHistory = [...(ticketData.assignmentHistory || [])];
    if (ticketData.status !== status) {
      statusHistory.push({
        id: Date.now(),
        from: ticketData.status,
        to: status,
        changedBy: 'Current User',
        timestamp: new Date().toISOString()
      });
    }
    if (ticketData.assignee !== assignee) {
      assignmentHistory.push({
        id: Date.now(),
        from: ticketData.assignee,
        to: assignee,
        changedBy: 'Current User',
        timestamp: new Date().toISOString()
      });
    }
    updatedTicket = {
      ...updatedTicket,
      statusHistory,
      assignmentHistory,
      comments: [...(ticketData.comments || []), ...stagedComments],
      internalNotes: [...(ticketData.internalNotes || []), ...stagedInternalNotes]
    };
    setTicketData(updatedTicket);
    onUpdate(updatedTicket);
    updateTicketInLocalStorage(updatedTicket);
    dispatch(addNotification({
      id: Date.now(),
      title: t('Ticket Updated'),
      message: t('Ticket has been updated.') + ` (${updatedTicket.ticketId})`,
      type: 'update',
      priority: updatedTicket.priority,
      department: updatedTicket.department,
      timestamp: new Date().toISOString(),
      isRead: false,
      isMarkRead: false,
      ticketId: updatedTicket.ticketId,
      assignee: updatedTicket.assignee || '',
      requiresAction: false
    }));
    stagedComments.forEach(comment => {
      dispatch(addNotification({
        id: Date.now() + Math.floor(Math.random()*1000),
        title: t('Comment Added'),
        message: t('A new comment was added.') + ` (${updatedTicket.ticketId})`,
        type: 'comment',
        priority: updatedTicket.priority,
        department: updatedTicket.department,
        timestamp: comment.timestamp,
        isRead: false,
        isMarkRead: false,
        ticketId: updatedTicket.ticketId,
        assignee: updatedTicket.assignee || '',
        requiresAction: false
      }));
    });
    stagedInternalNotes.forEach(note => {
      dispatch(addNotification({
        id: Date.now() + Math.floor(Math.random()*1000),
        title: t('Internal Note Added'),
        message: t('A new internal note was added.') + ` (${updatedTicket.ticketId})`,
        type: 'internal_note',
        priority: updatedTicket.priority,
        department: updatedTicket.department,
        timestamp: note.timestamp,
        isRead: false,
        isMarkRead: false,
        ticketId: updatedTicket.ticketId,
        assignee: updatedTicket.assignee || '',
        requiresAction: false
      }));
    });
    setStagedComments([]);
    setStagedInternalNotes([]);
    toast.success(t('Changes saved successfully!'));
    setEditMode(false);
  };

  const handleAddComment = () => {
    if (!editMode) {
      toast.error(t('Enable edit mode to add a comment'));
      return;
    }
    if (newComment.trim()) {
      const comment = {
        id: Date.now(),
        author: 'Current User',
        message: newComment,
        timestamp: new Date().toISOString(),
        isInternal: false
      };
      setStagedComments(prev => [...prev, comment]);
      setNewComment('');
    }
  };

  const handleAddInternalNote = () => {
    if (!editMode) {
      toast.error(t('Enable edit mode to add an internal note'));
      return;
    }
    if (newInternalNote.trim()) {
      const note = {
        id: Date.now(),
        author: 'Agent User',
        message: newInternalNote,
        timestamp: new Date().toISOString(),
        isInternal: true
      };
      setStagedInternalNotes(prev => [...prev, note]);
      setNewInternalNote('');
    }
  };

  // Removed stagedTicket specific handlers; Formik manages field state.
  const timelineEvents = [
    ...(ticketData.statusHistory || []).map(event => ({
      ...event,
      type: 'status',
      icon: 'pi pi-circle-on',
      color: '#607D8B',
      title: `Status changed from ${event.from} to ${event.to}`,
      subtitle: `by ${event.changedBy}`
    })),
    ...(ticketData.assignmentHistory || []).map(event => ({
      ...event,
      type: 'assignment',
      icon: 'pi pi-user',
      color: '#3F51B5',
      title: `Assigned from ${event.from} to ${event.to}`,
      subtitle: `by ${event.changedBy}`
    })),
    ...((ticketData.comments || []).map(comment => ({
      ...comment,
      type: 'comment',
      icon: 'pi pi-comment',
      color: '#4CAF50',
      title: 'Comment added',
      subtitle: `by ${comment.author}`,
      message: comment.message
    }))),
    ...((ticketData.internalNotes || []).map(note => ({
      ...note,
      type: 'internal_note',
      icon: 'pi pi-lock',
      color: '#FFC107',
      title: 'Internal note added',
      subtitle: `by ${note.author}`,
      message: note.message
    })))
  ].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  return (
    <FormikProvider value={formik}>
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {ticketData.ticketId}: {ticketData.subject}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <FaUser />
                <span>{t('Requester')}: {ticketData.requester}</span>
              </div>
              <div className="flex items-center gap-1">
                <FaCalendar />
                <span>{t('Created')}: {new Date(ticketData.createdDate).toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-1">
                <FaClock />
                <span>{t('Last Updated')}: {new Date(ticketData.lastUpdated).toLocaleString()}</span>
              </div>
            </div>
          </div>
          <Button
            onClick={() => {
              if (editMode) {
                // Cancel: revert form & staged items
                formik.resetForm();
                setStagedComments([]);
                setStagedInternalNotes([]);
              } else {
                // Enter edit: sync form with latest ticketData
                formik.setValues({
                  status: ticketData.status,
                  priority: ticketData.priority,
                  assignee: ticketData.assignee
                });
              }
              setEditMode(!editMode);
            }}
            label={editMode ? t('Cancel') : t('Edit')}
            icon={<FaEdit />}
            buttonType={editMode ? 'secondary' : 'primary'}
          />
        </div>

        {/* Status and Priority Badges */}
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">{t('Status')}:</span>
            {editMode ? (
              <DropdownField
                value={formik.values.status}
                handleChange={(e) => formik.setFieldValue('status', e.value)}
                options={ticketStatuses}
                className="min-w-32"
                useFormik={false}
              />
            ) : (
               <CustomChip
                 label={ticketData.status.replace(/([A-Z])/g, ' $1').trim()}
                 chipType={
                   ticketData.status === 'Open' ? 'blue' :
                   ticketData.status === 'InProgress' ? 'gradient' :
                   ticketData.status === 'Resolved' ? 'success' : 'secondary'
                 }
                 className="capitalize"
               />
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">{t('Priority')}:</span>
            {editMode ? (
              <DropdownField
                value={formik.values.priority}
                handleChange={(e) => formik.setFieldValue('priority', e.value)}
                options={ticketPriorities}
                className="min-w-28"
                useFormik={false}
              />
            ) : (
              <CustomChip
                label={ticketData.priority}
                chipType={
                  ticketData.priority === 'Critical' ? 'danger' :
                  ticketData.priority === 'High' ? 'warning' :
                  ticketData.priority === 'Medium' ? 'success' : 'secondary'
                }
                className="capitalize"
              />
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">{t('Department')}:</span>
            <CustomChip label={ticketData.department} chipType="blue" className="capitalize" />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">{t('Assigned To')}:</span>
            {editMode ? (
              <TextInput
                value={formik.values.assignee}
                onChange={(e) => formik.setFieldValue('assignee', e.target.value)}
                placeholder={t('Enter assignee name')}
                className="min-w-40"
                useFormik={false}
              />
            ) : (
               <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                 {ticketData.assignee}
               </span>
            )}
          </div>
        </div>

        {editMode && (
          <div className="flex gap-2 mt-4">
            <Button
              onClick={handleSave}
              label={t('Save Changes')}
              icon={<FaSave />}
              buttonType="primary"
            />
            <Button
              onClick={() => {
                formik.resetForm();
                setStagedComments([]);
                setStagedInternalNotes([]);
                setEditMode(false);
              }}
              label={t('Cancel')}
              buttonType="secondary"
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card title={t('Description')}>
            <div 
              className="prose max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: ticketData.description }}
            />
          </Card>

          {/* Department-Specific Information */}
          {Object.keys(ticketData.departmentSpecific || {}).length > 0 && (
            <Card title={t(`${ticketData.department} Information`)}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(ticketData.departmentSpecific).map(([key, value]) => (
                  <div key={key} className="flex flex-col">
                    <span className="text-sm font-medium text-gray-600 capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}:
                    </span>
                    <span className="text-gray-800">{value}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Attachments */}
          {ticketData.attachments && ticketData.attachments.length > 0 && (
            <Card title={t('Attachments')}>
              <div className="space-y-2">
                {ticketData.attachments.map((attachment, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 border rounded-lg">
                    <FaPaperclip className="text-gray-400" />
                    <span className="text-blue-600 hover:underline cursor-pointer">
                      {attachment}
                    </span>
                    <div className="ml-auto flex gap-2">
                      <Button
                        icon={<FaEye />}
                        buttonType="info"
                        size="small"
                        tooltip={t('View')}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Comments Section */}
          <Card title={t('Comments & Communication')}>
            <div className="space-y-4">
              {/* Existing Comments */}
              {ticketData.comments && ticketData.comments.length > 0 && (
                <div className="space-y-3">
                  {ticketData.comments.map((comment) => (
                    <div key={comment.id} className="border-l-4 border-blue-500 pl-4 py-2">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          <FaComments className="text-blue-500" />
                          <span className="font-medium text-gray-900">{comment.author}</span>
                          {comment.isInternal && (
                            <CustomChip label="Internal" chipType="warning" />
                          )}
                        </div>
                        <span className="text-sm text-gray-500">
                          {new Date(comment.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-gray-700">{comment.message}</p>
                    </div>
                  ))}
                </div>
              )}

              {editMode && stagedComments.length > 0 && (
                <div className="space-y-3">
                  <Divider />
                  <h4 className="text-sm font-semibold text-amber-600">{t('Staged Comments (Unsaved)')}</h4>
                  {stagedComments.map(comment => (
                    <div key={comment.id} className="border-l-4 border-amber-500 pl-4 py-2 bg-amber-50 relative">
                      <div className="flex justify-between items-start mb-1">
                        <div className="flex items-center gap-2">
                          <FaComments className="text-amber-600" />
                          <span className="font-medium text-gray-900">{comment.author}</span>
                          <CustomChip label={t('Unsaved')} chipType="warning" />
                        </div>
                        <span className="text-xs text-gray-500">{new Date(comment.timestamp).toLocaleString()}</span>
                      </div>
                      <p className="text-gray-700 text-sm">{comment.message}</p>
                      <button
                        type="button"
                        className="absolute top-1 right-2 text-xs text-red-500 hover:text-red-700"
                        onClick={() => setStagedComments(prev => prev.filter(c => c.id !== comment.id))}
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <Divider />

              {/* Add New Comment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('Add Public Comment')}
                </label>
                <InputTextarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  rows={3}
                  placeholder={t('Type your comment here...')}
                  className="w-full mb-2"
                  disabled={!editMode}
                />
                {!editMode && (
                  <p className="text-xs text-amber-600 mb-2">{t('Click Edit to enable adding comments')}</p>
                )}
                <Button
                  onClick={handleAddComment}
                  label={t('Add Comment')}
                  icon={<FaComments />}
                  buttonType="primary"
                  disabled={!editMode || !newComment.trim()}
                />
              </div>
            </div>
          </Card>

          {/* Internal Notes (Agent Only) */}
          <Card title={t('Internal Notes')} className="bg-yellow-50 border-yellow-200">
            <div className="space-y-4">
              {/* Existing Internal Notes */}
              {ticketData.internalNotes && ticketData.internalNotes.length > 0 && (
                <div className="space-y-3">
                  {ticketData.internalNotes.map((note) => (
                    <div key={note.id} className="border-l-4 border-yellow-500 pl-4 py-2 bg-yellow-50">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          <FaEye className="text-yellow-600" />
                          <span className="font-medium text-gray-900">{note.author}</span>
                          <CustomChip label="Agent Only" chipType="warning" />
                        </div>
                        <span className="text-sm text-gray-500">
                          {new Date(note.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-gray-700">{note.message}</p>
                    </div>
                  ))}
                </div>
              )}

              {editMode && stagedInternalNotes.length > 0 && (
                <div className="space-y-3">
                  <Divider />
                  <h4 className="text-sm font-semibold text-amber-600">{t('Staged Internal Notes (Unsaved)')}</h4>
                  {stagedInternalNotes.map(note => (
                    <div key={note.id} className="border-l-4 border-amber-500 pl-4 py-2 bg-amber-50 relative">
                      <div className="flex justify-between items-start mb-1">
                        <div className="flex items-center gap-2">
                          <FaEye className="text-amber-600" />
                          <span className="font-medium text-gray-900">{note.author}</span>
                          <CustomChip label={t('Unsaved')} chipType="warning" />
                        </div>
                        <span className="text-xs text-gray-500">{new Date(note.timestamp).toLocaleString()}</span>
                      </div>
                      <p className="text-gray-700 text-sm">{note.message}</p>
                      <button
                        type="button"
                        className="absolute top-1 right-2 text-xs text-red-500 hover:text-red-700"
                        onClick={() => setStagedInternalNotes(prev => prev.filter(n => n.id !== note.id))}
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <Divider />

              {/* Add Internal Note */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('Add Internal Note (Agent Only)')}
                </label>
                <InputTextarea
                  value={newInternalNote}
                  onChange={(e) => setNewInternalNote(e.target.value)}
                  rows={3}
                  placeholder={t('Type internal note here...')}
                  className="w-full mb-2"
                  disabled={!editMode}
                />
                {!editMode && (
                  <p className="text-xs text-amber-600 mb-2">{t('Click Edit to enable adding internal notes')}</p>
                )}
                <Button
                  onClick={handleAddInternalNote}
                  label={t('Add Internal Note')}
                  icon={<FaEye />}
                  buttonType="warning"
                  disabled={!editMode || !newInternalNote.trim()}
                />
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <Card title={t('Quick Actions')}>
            <div className="space-y-3">
              <Button
                label={t('Escalate Ticket')}
                icon={<FaArrowUp />}
                buttonType="warning"
                className="w-full"
              />
              <Button
                label={t('Merge with Related')}
                icon={<FaLink />}
                buttonType="info"
                className="w-full"
              />
              <Button
                label={t('Add Tags')}
                icon={<FaTags />}
                buttonType="secondary"
                className="w-full"
              />
            </div>
          </Card>

          {/* Related Tickets */}
          {ticketData.relatedTicket && (
            <Card title={t('Related Tickets')}>
              <div className="space-y-2">
                <div className="p-2 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <FaLink className="text-blue-500" />
                    <span className="text-blue-600 hover:underline cursor-pointer">
                      {ticketData.relatedTicket}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Activity Timeline */}
          <Card title={t('Activity Timeline')}>
            <Timeline 
              value={timelineEvents} 
              align="alternate" 
              className="customized-timeline"
              marker={(item) => (
                <span 
                  className="flex w-6 h-6 items-center justify-center text-white rounded-full z-10 shadow-md"
                  style={{ backgroundColor: item.color }}
                >
                  <i className={item.icon}></i>
                </span>
              )}
              content={(item) => (
                <Card className="mt-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <h6 className="font-medium text-gray-900">{item.title}</h6>
                      <p className="text-sm text-gray-600">{item.subtitle}</p>
                      {item.message && (
                        <p className="text-sm text-gray-700 mt-1">{item.message}</p>
                      )}
                    </div>
                    <small className="text-gray-500">
                      {new Date(item.timestamp).toLocaleString()}
                    </small>
                  </div>
                </Card>
              )}
            />
          </Card>
        </div>
      </div>
    </div>
    </FormikProvider>
  );
}